import { SessionContext } from '@/context/SessionContext';
import ConductionAPI from '@/javascript/conduction-api';
import { Utils } from '@/javascript/utils';
import { Anchor, Button, Group, Modal, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconAt, IconLock, IconUser } from '@tabler/icons-react';
import { useContext, useEffect, useState } from 'react';

interface RegistrationModalProps {
	showModal: boolean;
	setShowModal: (show: boolean) => void;
}

const RegistrationModal: React.FC<RegistrationModalProps> = (props) => {
	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("Modals must be a child of sessionContext.");
	}

	const { showModal, setShowModal } = props;

	const codeLength = 8;
	const adminCodeLength = 128;
	const [accessCode, setAccessCode] = useState<string | null>(null);
	const [accessCodeAccepted, setAccessCodeAccepted] = useState<boolean>(false);
	const [isStudentAccessCode, setIsStudentAccessCode] = useState(false);
	const [isClassroomAdminAccessCode, setIsClassroomAdminAccessCode] = useState(false);
	const [isConductionAdminAccessCode, setIsConductionAdminAccessCode] = useState(false);
	const [classroomName, setClassroomName] = useState<string | null>(null);

	const form = useForm({
		initialValues: {
			username: '',
			email: '',
			password: '',
			confirmPassword: '',
		},
		validate: {
			username: (value: string) => (value.trim().length === 0 ? 'Username is required' : null),
			email: (value: string) =>
				!isStudentAccessCode && !/^\S+@\S+\.\S+$/.test(value) ? 'Invalid email address' : null,
			password: (value: string) => {
				if (value.length < 6 || value.length > 30) return 'Password must be between 6 and 30 characters';
				if (!isStudentAccessCode && !/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
				if (!isStudentAccessCode && !/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
				if (!isStudentAccessCode && !/[0-9]/.test(value)) return 'Password must contain a number';
				if (!isStudentAccessCode && !/[^a-z\d]/i.test(value)) return 'Password must contain a special character';
				return null;
			},
			confirmPassword: (value: any, values: { password: any; }) =>
				value !== values.password ? 'Passwords do not match' : null,
		},
	});

	const handleRegister = async () => {
		if (!accessCode) {
			Utils.displayNotification("Access Code Missing", "Please enter a valid access code.", "red", <IconAlertCircle size={16} />);
			return;
		}

		const notificationId = Utils.displayNotification("Creating Account", "This may take a few moments...", "blue", "loading");

		try {
			let registrationResult;

			if (isStudentAccessCode) {
				registrationResult = await ConductionAPI.registerStudent(
					form.values.username,
					accessCode,
					form.values.password,
					form.values.confirmPassword
				);
			} else if (isClassroomAdminAccessCode) {
				registrationResult = await ConductionAPI.registerClassroomAdmin(
					form.values.username,
					accessCode,
					form.values.email,
					form.values.password,
					form.values.confirmPassword
				);
			} else if (isConductionAdminAccessCode) {
				registrationResult = await ConductionAPI.registerConductionAdmin(
					form.values.username,
					accessCode,
					form.values.email,
					form.values.password,
					form.values.confirmPassword
				);
			} else {
				Utils.updateNotification(notificationId, "Registration Failed", "Invalid access code type.", "red", <IconAlertCircle size={16} />);
				return;
			}

			if (registrationResult) {
				Utils.updateNotification(notificationId, "Success", "Account created successfully!", "green", <IconUser size={16} />);

				// Log the user in automatically
				if (await session.login(form.values.username, form.values.password, false)) {
					setShowModal(false);
				}
			} else {
				Utils.updateNotification(notificationId, "Username Taken", "This username may already be in use. Try a different one.", "red", <IconAlertCircle size={16} />);
			}
		} catch (error) {
			Utils.updateNotification(notificationId, "Registration Error", "An error occurred during account creation. Please try again.", "red", <IconAlertCircle size={16} />, true, false);
		}
	};

	const handleAccessCodeChange = () => {
		if (accessCode === null || (accessCode.length !== codeLength && accessCode.length !== adminCodeLength)) return;

		ConductionAPI.validateAccessCode(accessCode.toUpperCase() as string)
			.then((validation) => {
				if (validation.isValid) {
					if (validation.isLimitReached) {
						Utils.displayNotification("Invalid Code", "Unfortunately, the usage limit on this access code has been reached.", "red", <IconAlertCircle size={16} />);
					} else {
						setClassroomName(validation.classroomName ?? '');
						setIsStudentAccessCode(validation.isStudentAccessCode);
						setIsClassroomAdminAccessCode(validation.isClassroomAdminAccessCode);
						setIsConductionAdminAccessCode(validation.isConductionAdminAccessCode);
						setAccessCodeAccepted(true);
						Utils.displayNotification("Access Code Accepted", "Proceed with registration.", "green", <IconUser size={16} />);
					}
				} else {
					Utils.displayNotification("Invalid Code", "The access code provided is incorrect.", "red", <IconAlertCircle size={16} />);
				}
			}
			);
	};

	useEffect(() => handleAccessCodeChange(), [accessCode]);

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="Register"
			centered
			size="md"
			styles={{
				title: {
					fontSize: 26,
					fontVariant: "all-small-caps"
				}
			}}
		>
			<Stack>
				{accessCodeAccepted ? (
					<form onSubmit={form.onSubmit(handleRegister)}>
						<Stack>
							{classroomName && (
								<Text size="lg">
									Welcome! Now let's create your {classroomName} {isStudentAccessCode ? 'student' : isClassroomAdminAccessCode ? 'instructor' : 'admin'} account.
								</Text>
							)}

							<TextInput
								label={
									<Group>
										<IconUser size={20} />
										<Text>Username</Text>
									</Group>
								}
								{...form.getInputProps('username')}
								required
								withAsterisk={false}
							/>

							{!isStudentAccessCode && (
								<TextInput
									label={
										<Group>
											<IconAt size={20} />
											<Text>Email</Text>
										</Group>
									}
									{...form.getInputProps('email')}
									required
									withAsterisk={false}
								/>
							)}

							<PasswordInput
								label={
									<Group>
										<IconLock size={20} />
										<Text>Password</Text>
									</Group>
								}
								{...form.getInputProps('password')}
								required
								withAsterisk={false}
							/>

							<PasswordInput
								label={
									<Group>
										<IconLock size={20} />
										<Text>Confirm Password</Text>
									</Group>
								}
								{...form.getInputProps('confirmPassword')}
								required
								withAsterisk={false}
							/>

							<Button
								type="submit"
								radius="xl"
								size='sm'
								loading={form.submitting}
								color="red"
							>
								Register
							</Button>
						</Stack>
					</form>
				) : (
					<Stack>
						<Text size="lg">
							Enter your classroom's access code to begin creating your account.
						</Text>
						<TextInput
							label={
								<Group>
									<IconLock size={20} />
									<Text>Access Code</Text>
								</Group>
							}
							onChange={(event) => setAccessCode(event.target.value)}
							required
						/>
					</Stack>
				)}

				<Anchor
					component="button"
					type="button"
					onClick={() =>
						setShowModal(false)
					}
					size="s"
				>
					Already have an account? Click here to log in!
				</Anchor>
			</Stack>
		</Modal>
	);
};

export default RegistrationModal;
